import React, { useMemo, useRef} from 'react';
import JoditEditor from "jodit-pro-react";
import { LT_LANG } from '../../translations/jodit-translate'; // Import language file first
import type {
  IControlType,
  IJodit,
  IViewBased,
  IViewOptions,
  IToolbarButton,
  IFileBrowser,
  IFileBrowserOptions,
  Nullable
} from 'jodit/types';



interface TazEditorProps {
  value?: string;
  onChange: (value: string) => void;
  onBlur?: (e?: any) => void;
  id?: string;
  name?: string;
  onImportantToggle: (isImportant: boolean) => void;
}



const TazEditor: React.FC<TazEditorProps> = ({
                                               value,
                                               onChange,
                                               onBlur,
                                               id,
                                               name,
                                               onImportantToggle
                                             }) => {
  const importantButton: IControlType = {
    name: 'important',
    command: 'checkbox',
    tooltip: 'Svarbus laiškas',
    icon: `<svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
    <!-- Uploaded to SVGRepo https://www.svgrepo.com -->
    <title>ic_fluent_important_24_filled</title>
    <desc>Created with Sketch.</desc>
    <g id="🔍-System-Icons" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
        <g id="ic_fluent_important_24_filled" fill="#212121" fill-rule="nonzero">
            <path d="M12,17.0015 C13.3813,17.0015 14.5011,18.1213 14.5011,19.5026 C14.5011,20.8839 13.3813,22.0037 12,22.0037 C10.6187,22.0037 9.49888,20.8839 9.49888,19.5026 C9.49888,18.1213 10.6187,17.0015 12,17.0015 Z M11.999,2.00244 C14.1393,2.00244 15.8744,3.7375 15.8744,5.87781 C15.8744,8.71128 14.8844,12.4318 14.339,14.2756 C14.0294,15.322 13.0657,16.0039 12.0006,16.0039 C10.9332,16.0039 9.96846,15.3191 9.65995,14.2708 L9.43749451,13.4935787 C8.88270062,11.4994608 8.12366,8.3311 8.12366,5.87781 C8.12366,3.7375 9.85872,2.00244 11.999,2.00244 Z" id="🎨-Color"></path>
        </g>
    </g>
</svg>`,
    component: 'button',
    data: {
      active: false
    },
    exec: (
        editor: IJodit | IViewBased<IViewOptions>,
        current: Nullable<Node>,
        { control }: { control: IControlType }
    ): void => {
      if (control.data) {
        control.data.active = !control.data.active;
        onImportantToggle(control.data.active);
      }
    },
    isActive: (
        editor: IJodit | IViewBased<IViewOptions> | IFileBrowser<IFileBrowserOptions>,
        button: IToolbarButton
    ): boolean => {
      return !!button.control.data?.active;
    }
  };

  const editor = useRef(null)
  const config = useMemo(() => ({
    readonly: false,
    license: window?.env?.REACT_APP_JODIT_PRO_LICENSE || '',
    theme: 'default',
    language: 'lt',
    i18n: {
      lt: LT_LANG
    },
    toolbarSticky: true,
    height: "400px",
    buttons: "undo,redo,|,bold,italic,underline,strikethrough,|,ul,ol,todoList,|,font,fontsize,paragraph" +
        ",left, center, right, justify,|,indent,outdent,|,important,|,lineHeight,superscript,subscript,hr,|,eraser,brush,selectall",
    style: {
      fontFamily: 'Arial',
      fontSize: '14px'
    },
    controls: {
      important: importantButton,
      font: {
        component: 'select'
      },
      fontsize: {
        component: 'select'
      },
      paragraph: {
        component: 'select'
      },
    },
    cleanHTML: {
      removeEmptyElements: false
    },
    showPlaceholder: false,
    pasteFromWord: {
      enable: true,
      convertUnitsToPixel: true,
      allowedStyleProps: [
        'background',
        'background-color',
        'border',
        'border-.*',
        'color',
        'font-family',
        'font-size',
        'font-style',
        'font-weight',
        'height',
        'line-height',
        'list-style-type',
        'margin',
        'margin-bottom',
        'margin-left',
        'margin-right',
        'margin-top',
        'padding',
        'text-align',
        'text-decoration',
        'text-indent',
        'vertical-align',
        'width'
      ]
    }
  }), []);

  return (
      <JoditEditor
          id={id}
          name={name}
          ref={editor}
          value={value}
          config={config}
          tabIndex={1} // tabIndex of textarea
          onBlur={str => {
            onChange(str)
            if(onBlur) {
              onBlur(str)
            }
          }}
      />
  );
};

export default TazEditor;
